import {
  ref as storageRef,
  getBlob,
  getMetadata,
  deleteObject,
  type StorageError,
} from "firebase/storage";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import {
  getCachedImage,
  setCachedImage,
  generateKey,
} from "@/helpers/cache/image-cache";
import { CacheTypes } from "@/enums/cache-types";
import { SHORT_MEDIA_CACHE_TTL_MS } from "@/constants/shorts";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";

export type ShortMediaResult = {
  blob: Blob;
  url: string;
  fromCache: boolean;
};

/**
 * Downloads and caches a short video as a Blob.
 * 1. Checks IndexedDB cache first.
 * 2. If missed, downloads via getBlob from Firebase Storage.
 * 3. Caches blob in IndexedDB for 30 days.
 * 4. Returns local blob object URL and raw blob.
 */
export async function getShortVideoFromFirestorage(
  short: ShortDocument,
): Promise<ShortMediaResult> {
  const reference = storageRef(firebaseStorage, short.absolute_video_url);
  const cacheKey = generateKey(CacheTypes.ShortVideo, short._id, "mp4");

  // Try cache first
  const cached = await getCachedImage(
    cacheKey,
    reference,
    SHORT_MEDIA_CACHE_TTL_MS,
  );
  if (cached) {
    return {
      blob: cached.blob,
      url: cached.url,
      fromCache: true,
    };
  }

  // Fetch from Firebase Storage
  const blob = await getBlob(reference);
  const url = URL.createObjectURL(blob);

  // Cache blob in background
  try {
    let md5Hash: string | null = null;
    try {
      const metadata = await getMetadata(reference);
      md5Hash = metadata.md5Hash ?? null;
    } catch {
      // Continue without MD5 hash if metadata read fails
    }
    await setCachedImage(cacheKey, blob, SHORT_MEDIA_CACHE_TTL_MS, md5Hash);
  } catch (cacheError) {
    console.warn(`Failed to cache short video ${short._id}:`, cacheError);
  }

  return {
    blob,
    url,
    fromCache: false,
  };
}

/**
 * Downloads and caches a short thumbnail as a Blob.
 * 1. Checks IndexedDB cache first.
 * 2. If missed, downloads via getBlob from Firebase Storage.
 * 3. Caches blob in IndexedDB for 30 days.
 * 4. Returns local blob object URL and raw blob.
 */
export async function getShortThumbnailFromFirestorage(
  short: ShortDocument,
): Promise<ShortMediaResult> {
  if (!short.absolute_thumbnail_url) {
    throw new Error("Short has no thumbnail url");
  }

  const reference = storageRef(firebaseStorage, short.absolute_thumbnail_url);
  const cacheKey = generateKey(CacheTypes.ShortThumbnail, short._id, "jpg");

  // Try cache first
  const cached = await getCachedImage(
    cacheKey,
    reference,
    SHORT_MEDIA_CACHE_TTL_MS,
  );
  if (cached) {
    return {
      blob: cached.blob,
      url: cached.url,
      fromCache: true,
    };
  }

  // Fetch from Firebase Storage
  const blob = await getBlob(reference);
  const url = URL.createObjectURL(blob);

  // Cache blob in background
  try {
    let md5Hash: string | null = null;
    try {
      const metadata = await getMetadata(reference);
      md5Hash = metadata.md5Hash ?? null;
    } catch {
      // Continue without MD5 hash if metadata read fails
    }
    await setCachedImage(cacheKey, blob, SHORT_MEDIA_CACHE_TTL_MS, md5Hash);
  } catch (cacheError) {
    console.warn(`Failed to cache short thumbnail ${short._id}:`, cacheError);
  }

  return {
    blob,
    url,
    fromCache: false,
  };
}

/**
 * Best-effort deletion of short media (video and poster) from Firebase Storage.
 * Missing files are ignored; other failures are logged but never thrown.
 */
export async function deleteShortMedia(paths: string[]): Promise<void> {
  await Promise.all(
    paths.map(async (path) => {
      try {
        await deleteObject(storageRef(firebaseStorage, path));
      } catch (error) {
        const storageError = error as StorageError;
        if (storageError.code !== "storage/object-not-found") {
          console.error(`Failed to delete short media ${path}:`, error);
        }
      }
    }),
  );
}
